import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { UseGradualRollout } from '../../../../../../utilities/useGradualRollout';
import { SetTimer, timerUtils } from '../../../../../../utilities/timer';
import { AppState } from '../../../../../../stores/redux-store';
import { CredentialsEntity } from '../../../../../../models/credentials';
import { useThirdPartyRedirector } from '../../../../../../utilities/thirdparty-redirector/thirdparty-redirector';
import { usePostRedirectMessage } from '../parent-redirect-messenger';
import {
  AwaitingTppPendingNoAutoRedirectScreen as AwaitingTppPendingNoAutoRedirectScreenImport,
  AwaitingTppPendingScreenNoAutoRedirectProps,
} from '../../AwaitingMobileBankIdAuthenticationContainer/molecules/PendingThirdPartyAuthentication/AwaitingTppPendingScreen';
import { stopPollingCredentials as stopPollingCredentialsImport } from '../../../../../../connectivity/services/credentials';
import { resetCredentials as resetCredentialsConnect } from '../../../../../../stores/credentials-store';
import { useSelectedProvider } from '../../../../../App/ProviderContainer/useSelectedProvider';
import {
  AwaitingTppInstructionScreen as AwaitingTppInstructionScreenImport,
  AwaitingTppInstructionScreenProps,
} from '../../../AwaitingTppInstructionScreen';
import {
  AwaitingTppRedirectScreen as AwaitingTppRedirectScreenImport,
  AwaitingTppRedirectScreenProps,
} from '../../../AwaitingTppRedirectScreen';

export type AwaitingTppIframeProps = {
  clientName: string;
  credentials: CredentialsEntity;
  setTimer?: SetTimer;
  isClientVerified: boolean;
  stopPollingCredentials?: typeof stopPollingCredentialsImport;
  AwaitingTppRedirectScreen?: React.ComponentType<AwaitingTppRedirectScreenProps>;
  AwaitingTppPendingScreen?: React.ComponentType<AwaitingTppPendingScreenNoAutoRedirectProps>;
  AwaitingTppInstructionScreen?: React.ComponentType<AwaitingTppInstructionScreenProps>;
  isMobile?: boolean;
  useGradualRollout?: UseGradualRollout;
};

export const AwaitingTppIframePresenter: React.FC<AwaitingTppIframeProps & PropsFromRedux> = ({
  credentials,
  clientName,
  setTimer = timerUtils.setTimer,
  resetCredentials,
  isClientVerified,
  stopPollingCredentials = stopPollingCredentialsImport,
  AwaitingTppRedirectScreen = AwaitingTppRedirectScreenImport,
  AwaitingTppPendingScreen = AwaitingTppPendingNoAutoRedirectScreenImport,
  AwaitingTppInstructionScreen = AwaitingTppInstructionScreenImport,
  isMobile = userAgent.isMobile,
}) => {
  const history = useHistory();
  const { selectedProvider: provider } = useSelectedProvider();
  const { thirdPartyRedirector } = useThirdPartyRedirector(credentials);
  usePostRedirectMessage(thirdPartyRedirector);

  const [showPendingScreen, setShowPendingScreen] = useState(false);
  const shouldShowConsentRedirectScreen = isMobile;

  useEffect(() => {
    return () => setShowPendingScreen(false);
  }, [credentials.supplementalInformation]);

  const onBackPendingThirdPartyButton = () => {
    resetCredentials();
    stopPollingCredentials();
    history.goBack();
  };

  const onClickHandler = () => {
    setTimer(() => {
      setShowPendingScreen(true);
    }, 500);

    thirdPartyRedirector?.navigate();
  };

  if (shouldShowConsentRedirectScreen) {
    return (
      <AwaitingTppRedirectScreen
        provider={provider}
        clientName={clientName || ''}
        onBack={onBackPendingThirdPartyButton}
        isVerified={isClientVerified}
        onClick={onClickHandler}
      />
    );
  }

  if (showPendingScreen) {
    return (
      <AwaitingTppPendingScreen
        providerName={provider.displayName}
        onBack={onBackPendingThirdPartyButton}
        credentials={credentials}
        onClick={onClickHandler}
      />
    );
  }

  return (
    <AwaitingTppInstructionScreen
      onBack={onBackPendingThirdPartyButton}
      clientName={clientName}
      onClick={onClickHandler}
      provider={provider}
      isVerified={isClientVerified}
    />
  );
};

/* istanbul ignore next: mapStateToProps */
const mapStateToProps = (state: AppState) => {
  return {
    user: state.user.user,
    isClientVerified: Boolean(state.oauth.description?.verified),
  };
};

const mapDispatchToProps = {
  resetCredentials: resetCredentialsConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const AwaitingTppIframe = connector(AwaitingTppIframePresenter);
